import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Stores non-serializable objects globally (not in Redux)
export const canvasMap = new Map<string, HTMLCanvasElement>();
export const ctxMap = new Map<string, CanvasRenderingContext2D | null>();
export const mediaRecorderMap = new Map<string, MediaRecorder | null>();
export const recordedChunksMap = new Map<string, BlobPart[]>();
export const videoStreamsMap = new Map<string, MediaStream>();

interface WebSocketState {
  liveStream: any
  snapShots: any
  recordedVideo: any
  settingFile: any
  isLiveStreamActive: boolean,
  pendingVideos: any[];
  commandStatus: any;
}

const initialState: WebSocketState = {
  liveStream: {},
  snapShots: {},
  recordedVideo: {},
  settingFile: {},
  isLiveStreamActive: false,
  pendingVideos: [],
  commandStatus: null
}

const WebSocketSlice = createSlice({
  name: 'webSocket',
  initialState,
  reducers: {
    setLiveStream(state, action: PayloadAction<any>) {
      state.liveStream = action.payload
    },

    toggleLiveStream: (state, action) => {
      state.isLiveStreamActive = action.payload
    },

    setSnapShots(state, action: PayloadAction<any>) {
      state.snapShots = action.payload
    },

    // addSnapShots(state, action: PayloadAction<string[]>) {
    //     state.snapShots = [...state.snapShots, ...action.payload];
    // },

    setRecordedVideo(state, action: PayloadAction<any>) {
      state.recordedVideo = action.payload
    },

    setSettingFile(state, action: PayloadAction<any>) {
      state.settingFile = action.payload
    },

    setCommandStatus(state, action: PayloadAction<any>) {
      state.commandStatus = action.payload;
    },

    startRecording: (_, action: PayloadAction<string[]>) => {
      action.payload.forEach((cameraId) => {
        if (!canvasMap.has(cameraId)) {
          const canvas = document.createElement("canvas");
          canvasMap.set(cameraId, canvas);
          ctxMap.set(cameraId, canvas.getContext("2d"));
          videoStreamsMap.set(cameraId, canvas.captureStream(60)); // 60 FPS
        }

        recordedChunksMap.set(cameraId, []);
        const recorder = new MediaRecorder(videoStreamsMap.get(cameraId)!, {
          mimeType: "video/webm; codecs=vp9",
        });

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksMap.get(cameraId)!.push(event.data);
          }
        };

        recorder.start();
        mediaRecorderMap.set(cameraId, recorder);
      });
    },

    stopRecording: (state) => {
      mediaRecorderMap.forEach((recorder, cameraId) => {
        if (recorder) {
          recorder.stop();
          state.pendingVideos.push(cameraId);
        }
      });
    },

    saveVideoRecording: (state, action: PayloadAction<string>) => {
      const cameraId = action.payload;
      const chunks = recordedChunksMap.get(cameraId);

      if (!chunks || chunks.length === 0) return;

      const recordedBlob = new Blob(chunks, { type: "video/webm" });
      const videoURL = URL.createObjectURL(recordedBlob);
      const a = document.createElement("a");
      a.href = videoURL;
      a.download = `${cameraId}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Cleanup
      state.pendingVideos = state.pendingVideos.filter((id) => id !== cameraId);
      recordedChunksMap.delete(cameraId);
    },

    clearVideoRecording: (state, action: PayloadAction<string | undefined>) => {
      const cameraId = action.payload;

      if (cameraId) {
        // Clear a specific video
        state.pendingVideos = state.pendingVideos.filter((id) => id !== cameraId);
        recordedChunksMap.delete(cameraId);
      } else {
        // Clear all videos
        state.pendingVideos = [];
        recordedChunksMap.clear();
      }
    },
  },
})

export const { setLiveStream, toggleLiveStream, setSnapShots, setRecordedVideo, setSettingFile, setCommandStatus,
  startRecording, stopRecording, saveVideoRecording, clearVideoRecording
} =
  WebSocketSlice.actions
export default WebSocketSlice.reducer
